import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectPropsBase {
  options: SelectOption[];
  placeholder?: string;
  label?: string;
  inlineLabel?: boolean;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  buttonClassName?: string;
  error?: string;
  required?: boolean;
}

interface SingleSelectProps extends SelectPropsBase {
  multiple?: false;
  value?: string;
  onChange: (value: string) => void;
}

interface MultiSelectProps extends SelectPropsBase {
  multiple: true;
  value?: string[];
  onChange: (value: string[]) => void;
}

type SelectProps = SingleSelectProps | MultiSelectProps;

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  label,
  disabled = false,
  loading = false,
  className = '',
  buttonClassName = '',
  inlineLabel = false,
  error,
  required = false,
  multiple = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Get selected option(s)
  const selectedOptions = multiple 
    ? options.filter(option => (value as string[] | undefined)?.includes(option.value))
    : [options.find(option => option.value === value)].filter(Boolean) as SelectOption[];
  
  const selectedOption = !multiple ? options.find(option => option.value === value) : undefined;

  // Calculate dropdown position
  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = Math.min(300, options.length * 44 + 8); // Max 300px, or calculated height
    const dropdownWidth = rect.width;
    
    let top = rect.bottom + 4;
    let left = rect.left;
    
    // Check if dropdown fits below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      // Show above
      top = rect.top - dropdownHeight - 4;
    }
    
    // Ensure dropdown doesn't go off screen horizontally
    if (left + dropdownWidth > viewportWidth - 8) {
      left = viewportWidth - dropdownWidth - 8;
    }
    if (left < 8) {
      left = 8;
    }
    
    // Ensure dropdown doesn't go off screen vertically
    if (top < 8) {
      top = 8;
    }
    if (top + dropdownHeight > viewportHeight - 8) {
      top = viewportHeight - dropdownHeight - 8;
    }
    
    setDropdownPosition({ top, left, width: dropdownWidth });
  }, [options.length]);

  // Update position when opening
  useEffect(() => {
    if (isOpen) {
      calculatePosition();
      
      // Recalculate on scroll and resize
      const handleUpdate = () => {
        if (isOpen) {
          calculatePosition();
        }
      };
      
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isOpen, options.length, calculatePosition]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIsOpen(true);
        if (multiple) {
          const firstSelected = (value as string[] | undefined)?.[0];
          setHighlightedIndex(firstSelected ? options.findIndex(opt => opt.value === firstSelected) : 0);
        } else {
          setHighlightedIndex(value ? options.findIndex(opt => opt.value === value) : 0);
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const next = prev < options.length - 1 ? prev + 1 : 0;
          return next;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const next = prev > 0 ? prev - 1 : options.length - 1;
          return next;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex] && !options[highlightedIndex].disabled) {
          handleOptionSelect(options[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        buttonRef.current?.focus();
        break;
      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && dropdownRef.current) {
      const optionElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (optionElement) {
        optionElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Handle option selection
  const handleOptionSelect = (optionValue: string) => {
    if (multiple) {
      const currentValues = (value as string[] | undefined) || [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      (onChange as (value: string[]) => void)(newValues);
      // Don't close dropdown in multi-select mode
      setHighlightedIndex(-1);
    } else {
      (onChange as (value: string) => void)(optionValue);
      setIsOpen(false);
      setHighlightedIndex(-1);
      buttonRef.current?.focus();
    }
  };

  return (
    <div className={`relative ${inlineLabel ? 'flex items-center gap-3' : 'space-y-2'} ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className={`text-sm font-medium flex-shrink-0 ${inlineLabel ? 'w-24' : 'block'} ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Select Button */}
      <div className={inlineLabel ? 'flex-1 min-w-0' : ''}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        className={`relative w-full rounded-lg shadow-sm focus:ring-mirsal-green focus:border-mirsal-green text-sm transition-all duration-200 hover:border-gray-400 focus:ring-2 py-2.5 px-4 pr-10 cursor-pointer appearance-none border text-left min-h-14 ${
          theme === 'dark' 
            ? 'border-gray-600 bg-gray-800 text-white hover:bg-gray-700 hover:border-gray-500 disabled:hover:bg-gray-800' 
            : 'border-gray-300 bg-white text-gray-900 hover:bg-gray-50 hover:border-gray-400 disabled:hover:bg-white'
        } ${
          disabled || loading ? 'opacity-50 cursor-not-allowed' : ''
        } ${
          error ? 'border-red-500' : ''
        } ${
          isOpen ? 'ring-2 ring-mirsal-green border-mirsal-green' : ''
        } ${buttonClassName}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label || placeholder}
      >
        {multiple ? (
          <div className="flex flex-wrap items-center gap-1.5 min-h-[24px]">
            {selectedOptions.length === 0 ? (
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {placeholder}
              </span>
            ) : (
              selectedOptions.map((option) => (
                <span
                  key={option.value}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    option.value === 'admin' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                    option.value === 'host' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                    option.value === 'guest' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                    'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                  }`}
                >
                  {option.label}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionSelect(option.value);
                    }}
                    className="ml-1.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-opacity-80"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            )}
          </div>
        ) : (
          <span className={`block truncate ${
            selectedOption ? '' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {loading ? 'Loading...' : selectedOption ? selectedOption.label : placeholder}
          </span>
        )}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {loading ? (
            <div className={`animate-spin rounded-full h-4 w-4 border-2 border-t-transparent ${
              theme === 'dark' ? 'border-gray-400' : 'border-gray-500'
            }`}></div>
          ) : (
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            } ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
          )}
        </div>
      </button>
      </div>

      {/* Dropdown Portal */}
      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className={`fixed z-[10001] rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${
            theme === 'dark' 
              ? 'bg-gray-800 border border-gray-700' 
              : 'bg-white border border-gray-200'
          }`}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxHeight: '300px',
            overflowY: 'auto'
          }}
          role="listbox"
        >
          <div className="py-1">
            {options.length === 0 ? (
              <div className={`px-4 py-3 text-sm text-center ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                No options available
              </div>
            ) : (
              options.map((option, index) => {
                const isSelected = multiple
                  ? (value as string[] | undefined)?.includes(option.value) || false
                  : option.value === value;
                const isHighlighted = index === highlightedIndex;
                const isDisabled = option.disabled;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => !isDisabled && handleOptionSelect(option.value)}
                    onMouseEnter={() => !isDisabled && setHighlightedIndex(index)}
                    disabled={isDisabled}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors duration-150 ${
                      isSelected
                        ? theme === 'dark'
                          ? 'bg-mirsal-green/20 text-mirsal-green'
                          : 'bg-mirsal-green/10 text-mirsal-green'
                        : isHighlighted
                        ? theme === 'dark'
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-900'
                        : theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    } ${
                      isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="flex items-center space-x-2 flex-1">
                      {multiple && (
                        <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                          isSelected
                            ? 'bg-mirsal-green border-mirsal-green'
                            : theme === 'dark'
                            ? 'border-gray-500'
                            : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <Check className={`h-3 w-3 ${theme === 'dark' ? 'text-white' : 'text-white'}`} />
                          )}
                        </div>
                      )}
                      <span className="truncate">{option.label}</span>
                    </div>
                    {!multiple && isSelected && (
                      <Check className={`h-4 w-4 ml-2 flex-shrink-0 ${
                        theme === 'dark' ? 'text-mirsal-green' : 'text-mirsal-green'
                      }`} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default Select;

